import EmailIcon from '@mui/icons-material/Email';
import FilterListIcon from '@mui/icons-material/FilterList';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDebounce } from '@shared/hooks/useDebounce';
import { format, isValid, parseISO, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  type EmailListItem,
  useGetAutoSummaryGlobalStatusQuery,
  useListEmailCategoriesQuery,
  useListEmailsQuery,
  useSetAutoSummaryGlobalStatusMutation,
} from './api/emailsApi';

// Helper to format Date to YYYY-MM-DD for input[type="date"]
const toDateInputValue = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

export default function SentEmailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'enable' | 'disable' | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  // Emergency stop state
  const {
    data: globalStatus,
    isLoading: isLoadingGlobalStatus,
    isFetching: isFetchingGlobalStatus,
  } = useGetAutoSummaryGlobalStatusQuery();
  const [setGlobalStatus, { isLoading: isTogglingGlobalStatus }] =
    useSetAutoSummaryGlobalStatusMutation();

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 1;
  });
  const [status, setStatus] = useState(() => searchParams.get('status') || '');
  const [coachSearch, setCoachSearch] = useState(() => searchParams.get('coach') || '');
  const [memberSearch, setMemberSearch] = useState(() => searchParams.get('member') || '');
  const [fromDateStr, setFromDateStr] = useState(() => {
    const d = searchParams.get('from_date');
    return d || toDateInputValue(subDays(new Date(), 7));
  });
  const [toDateStr, setToDateStr] = useState(() => {
    return searchParams.get('to_date') || '';
  });
  const [category, setCategory] = useState(() => searchParams.get('category') || '');

  // Debounce search inputs to avoid excessive API calls
  const debouncedCoachSearch = useDebounce(coachSearch, 300);
  const debouncedMemberSearch = useDebounce(memberSearch, 300);

  const { data: categoriesData } = useListEmailCategoriesQuery();

  const perPage = 25;

  // Update URL when filters change (use debounced values for search)
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (status) params.set('status', status);
    if (debouncedCoachSearch) params.set('coach', debouncedCoachSearch);
    if (debouncedMemberSearch) params.set('member', debouncedMemberSearch);
    if (fromDateStr) params.set('from_date', fromDateStr);
    if (toDateStr) params.set('to_date', toDateStr);
    if (category) params.set('category', category);
    setSearchParams(params, { replace: true });
  }, [
    page,
    status,
    category,
    debouncedCoachSearch,
    debouncedMemberSearch,
    fromDateStr,
    toDateStr,
    setSearchParams,
  ]);

  const { data, isLoading, isFetching, error, refetch } = useListEmailsQuery({
    page,
    per_page: perPage,
    status: status || undefined,
    coach: debouncedCoachSearch || undefined,
    member: debouncedMemberSearch || undefined,
    from_date: fromDateStr || undefined,
    to_date: toDateStr || undefined,
    category: category || undefined,
  });

  const emails = data?.emails ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const handleClearFilters = useCallback(() => {
    setPage(1);
    setStatus('');
    setCoachSearch('');
    setMemberSearch('');
    setCategory('');
    setFromDateStr(toDateInputValue(subDays(new Date(), 7)));
    setToDateStr('');
  }, []);

  const handleToggleEmergencyStop = useCallback((action: 'enable' | 'disable') => {
    setPendingAction(action);
    setToggleError(null);
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!pendingAction) return;
    setToggleError(null);
    try {
      await setGlobalStatus({ enabled: pendingAction === 'enable' }).unwrap();
      setConfirmDialogOpen(false);
      setPendingAction(null);
    } catch (err) {
      const errorMessage =
        (err as { data?: { error?: string } })?.data?.error ||
        'Failed to update email status. Please try again.';
      setToggleError(errorMessage);
    }
  }, [pendingAction, setGlobalStatus]);

  const handleCancelToggle = useCallback(() => {
    setConfirmDialogOpen(false);
    setPendingAction(null);
    setToggleError(null);
  }, []);

  const formatDate = useCallback((dateStr: string) => {
    const date = parseISO(dateStr);
    if (!isValid(date)) {
      return '—';
    }
    return format(date, 'MMM d, yyyy h:mm a');
  }, []);

  const truncate = useCallback((str: string | undefined | null, len: number) => {
    if (!str) return '—';
    return str.length > len ? str.slice(0, len) + '...' : str;
  }, []);

  // Memoize the search adornment to avoid recreating on every render
  const searchAdornment = useMemo(
    () => (
      <InputAdornment position="start">
        <SearchIcon fontSize="small" />
      </InputAdornment>
    ),
    []
  );

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <EmailIcon />
        <Typography variant="h4" component="h1">
          Sent Emails
        </Typography>
        {(isLoading || isFetching) && <CircularProgress size={20} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View all summary emails sent by the system.
      </Typography>

      {/* Emergency Stop Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderColor: globalStatus?.enabled === false ? 'error.main' : 'divider',
          bgcolor: globalStatus?.enabled === false ? 'error.50' : 'background.paper',
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {globalStatus?.enabled === false ? (
                <StopCircleIcon color="error" sx={{ fontSize: 32 }} />
              ) : (
                <PlayCircleIcon color="success" sx={{ fontSize: 32 }} />
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Auto-Summary Emails
                  {(isLoadingGlobalStatus || isFetchingGlobalStatus) && (
                    <CircularProgress size={14} sx={{ ml: 1 }} />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {globalStatus?.enabled === false
                    ? 'Emails are currently STOPPED. No summary emails will be sent.'
                    : 'Emails are enabled and will be sent normally.'}
                </Typography>
              </Box>
            </Box>
            <Box>
              {globalStatus?.enabled === false ? (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    isTogglingGlobalStatus ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PlayCircleIcon />
                    )
                  }
                  onClick={() => handleToggleEmergencyStop('enable')}
                  disabled={isTogglingGlobalStatus || isLoadingGlobalStatus}
                >
                  Resume Emails
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={
                    isTogglingGlobalStatus ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PauseCircleIcon />
                    )
                  }
                  onClick={() => handleToggleEmergencyStop('disable')}
                  disabled={isTogglingGlobalStatus || isLoadingGlobalStatus}
                >
                  Stop All Emails
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load emails. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDateStr}
            onChange={(e) => {
              setFromDateStr(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDateStr}
            onChange={(e) => {
              setToDateStr(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="skipped">Skipped</MenuItem>
            </Select>
          </FormControl>
          <TextField
            value={coachSearch}
            onChange={(e) => {
              setCoachSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Coach name or email"
            size="small"
            InputProps={{ startAdornment: searchAdornment }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            value={memberSearch}
            onChange={(e) => {
              setMemberSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Member name"
            size="small"
            InputProps={{ startAdornment: searchAdornment }}
            sx={{ minWidth: 180 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {(categoriesData?.categories ?? []).map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleClearFilters}
            size="small"
          >
            Clear Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            size="small"
          >
            Refresh
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Page {page} of {totalPages} • {total} emails
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Sent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Coach</TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Subject</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emails.map((email: EmailListItem) => (
                <TableRow key={email.id} hover>
                  <TableCell>{formatDate(email.sent_at)}</TableCell>
                  <TableCell>
                    {email.status === 'sent' ? (
                      <Chip label="Sent" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Skipped" size="small" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {email.category || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={email.coach_email}>
                      <Link
                        to={`/admin/coaches/${email.coach_person_id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ '&:hover': { textDecoration: 'underline' } }}
                        >
                          {email.coach_name}
                        </Typography>
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{email.member_name || '—'}</TableCell>
                  <TableCell>
                    {email.status === 'skipped' ? (
                      <Typography variant="body2" fontStyle="italic" color="text.secondary">
                        {truncate(email.skip_reason, 50)}
                      </Typography>
                    ) : (
                      truncate(email.email_subject, 50)
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {emails.length === 0 && !isLoading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No emails found matching your filters.</Typography>
          </Box>
        )}
      </Paper>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Showing {emails.length} of {total}
        </Typography>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelToggle}>
        <DialogTitle>
          {pendingAction === 'disable' ? 'Stop All Summary Emails?' : 'Resume Summary Emails?'}
        </DialogTitle>
        <DialogContent>
          {toggleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {toggleError}
            </Alert>
          )}
          <DialogContentText>
            {pendingAction === 'disable'
              ? 'This will immediately stop all auto-summary emails from being sent. Coaches will not receive any summary emails until you resume them. This affects all coaches globally.'
              : 'This will resume sending auto-summary emails to coaches. Emails will be sent according to the normal schedule.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelToggle}>Cancel</Button>
          <Button
            onClick={handleConfirmToggle}
            color={pendingAction === 'disable' ? 'error' : 'success'}
            variant="contained"
            disabled={isTogglingGlobalStatus}
          >
            {isTogglingGlobalStatus ? (
              <CircularProgress size={20} color="inherit" />
            ) : pendingAction === 'disable' ? (
              'Stop Emails'
            ) : (
              'Resume Emails'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
